/**
 * Widget 内联样式表（作为 CSS 字符串注入 shadow root）
 *
 * ============ 样式定制 ============
 * Shadow DOM 会隔离宿主页的类名/元素样式，但 CSS 自定义属性（--ct-*）能穿透。
 * 宿主页在 #chat-toriri-widget-root 上设置这些变量就能覆盖默认样式。
 *
 * 完整变量列表见 README.md 的"Widget 样式定制"章节。
 */
export const widgetCss = `
/* =========================================
 *  CSS 变量默认值
 *  用户在 #chat-toriri-widget-root 上覆盖即可自定义
 *
 *  实现细节：
 *  - :host 命中嵌入式 widget 的 Shadow DOM host 元素
 *  - :root 命中独立对话页（无 Shadow DOM）
 *  - 不写 .ct-root！否则会以直接声明的形式覆盖掉宿主页通过继承传进来的变量
 * ========================================= */
:host, :root {
  /* ---- 主色系 ---- */
  --ct-brand: #07C160;
  --ct-brand-hover: #06AD56;

  /* ---- 气泡按钮 ---- */
  --ct-bubble-bg: var(--ct-brand);
  --ct-bubble-color: white;
  --ct-bubble-size: 56px;
  --ct-bubble-radius: 28px;
  --ct-bubble-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
  --ct-bubble-shadow-hover: 0 8px 24px rgba(0, 0, 0, 0.2);
  --ct-bubble-bottom: 24px;
  --ct-bubble-right: 24px;
  --ct-bubble-left: auto;
  --ct-bubble-top: auto;

  /* ---- 面板容器 ---- */
  --ct-panel-bg: #FFFFFF;
  --ct-panel-radius: 12px;
  --ct-panel-shadow: 0 12px 40px rgba(0, 0, 0, 0.16);
  --ct-panel-width: 380px;
  --ct-panel-height: 600px;

  /* ---- 顶栏 ---- */
  --ct-header-bg: #FFFFFF;
  --ct-header-text: #191919;
  --ct-header-sub-text: #B3B3B3;
  --ct-header-border: rgba(0, 0, 0, 0.06);

  /* ---- 消息区 ---- */
  --ct-body-bg: #F7F7F7;
  --ct-muted: #B3B3B3;

  --ct-self-bubble: #95EC69;
  --ct-self-bubble-text: #191919;
  --ct-agent-bubble: #FFFFFF;
  --ct-agent-bubble-text: #191919;
  --ct-agent-bubble-border: rgba(0, 0, 0, 0.04);

  /* ---- FAQ 胶囊 ---- */
  --ct-faq-bg: #F2F7F5;
  --ct-faq-text: var(--ct-brand);
  --ct-faq-border: rgba(7, 193, 96, 0.3);
  --ct-faq-hover-bg: #E6F5EC;

  /* ---- 输入区 ---- */
  --ct-input-bg: #FFFFFF;
  --ct-input-border-top: rgba(0, 0, 0, 0.06);
  --ct-textarea-bg: #FAFAFA;
  --ct-textarea-focus-bg: #FFFFFF;
  --ct-textarea-text: #191919;
  --ct-textarea-border: rgba(0, 0, 0, 0.09);
  --ct-textarea-focus: var(--ct-brand);

  --ct-send-bg: var(--ct-brand);
  --ct-send-hover-bg: var(--ct-brand-hover);
  --ct-send-color: white;
  --ct-send-disabled-bg: rgba(0, 0, 0, 0.05);
  --ct-send-disabled-color: #BDBDBD;
  --ct-attach-color: #999;
  --ct-attach-hover-bg: rgba(0, 0, 0, 0.05);

  /* ---- 字体 ---- */
  --ct-font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
}

/* 排版基线（不用 all: initial，避免破坏自定义属性继承） */
.ct-root, .ct-bubble {
  font-family: var(--ct-font-family);
  color: var(--ct-agent-bubble-text);
  font-size: 14px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}
.ct-root, .ct-root *, .ct-root *::before, .ct-root *::after {
  box-sizing: border-box;
}

/* =========================================
 *  气泡按钮
 * ========================================= */
.ct-bubble {
  position: fixed;
  bottom: var(--ct-bubble-bottom);
  right: var(--ct-bubble-right);
  left: var(--ct-bubble-left);
  top: var(--ct-bubble-top);
  width: var(--ct-bubble-size);
  height: var(--ct-bubble-size);
  border-radius: var(--ct-bubble-radius);
  border: none;
  background: var(--ct-bubble-bg);
  color: var(--ct-bubble-color);
  box-shadow: var(--ct-bubble-shadow);
  cursor: pointer;
  z-index: 2147483000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform .2s, box-shadow .2s;
}
.ct-bubble:hover { transform: translateY(-2px); box-shadow: var(--ct-bubble-shadow-hover); }
.ct-bubble svg { width: 26px; height: 26px; }

/* =========================================
 *  面板
 * ========================================= */
.ct-panel {
  background: var(--ct-panel-bg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.ct-panel-embedded {
  position: fixed;
  bottom: var(--ct-bubble-bottom);
  right: var(--ct-bubble-right);
  left: var(--ct-bubble-left);
  top: var(--ct-bubble-top);
  width: var(--ct-panel-width);
  height: var(--ct-panel-height);
  max-height: calc(100vh - 48px);
  border-radius: var(--ct-panel-radius);
  box-shadow: var(--ct-panel-shadow);
  z-index: 2147483000;
  animation: ct-in .2s cubic-bezier(0.22, 1, 0.36, 1);
}

.ct-panel-page {
  width: 100%;
  height: 100%;
  min-height: 0;
  max-width: 480px;
  border-radius: var(--ct-panel-radius);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  margin: 0 auto;
}

@keyframes ct-in {
  from { opacity: 0; transform: translateY(12px) scale(0.98); transform-origin: bottom right; }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 520px) {
  .ct-panel-page {
    max-width: none;
    border-radius: 0;
    box-shadow: none;
  }
  /* 移动端嵌入式面板：几乎全屏 */
  .ct-panel-embedded {
    width: calc(100vw - 24px);
    height: calc(100vh - 100px);
    right: 12px;
    left: auto;
    bottom: 88px;
  }
}

/* =========================================
 *  顶栏
 * ========================================= */
.ct-header {
  height: 52px;
  padding: 0 12px 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--ct-header-bg);
  border-bottom: 1px solid var(--ct-header-border);
  flex-shrink: 0;
}
.ct-header-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--ct-brand), #0AC29D);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  overflow: hidden;
}
.ct-header-avatar img { width: 100%; height: 100%; object-fit: cover; }
.ct-header-info { flex: 1; min-width: 0; }
.ct-header-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--ct-header-text);
  display: flex;
  align-items: center;
  gap: 6px;
}
.ct-header-dot {
  width: 6px; height: 6px; border-radius: 50%;
  /* 客服在线指示灯：AI 常驻始终绿色，不跟品牌色 */
  background: #07C160;
  box-shadow: 0 0 4px rgba(7, 193, 96, 0.5);
}
.ct-header-title { font-size: 11px; color: var(--ct-header-sub-text); }
.ct-close {
  width: 32px; height: 32px;
  border: none; background: transparent;
  color: var(--ct-muted); cursor: pointer;
  border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
}
.ct-close:hover { background: rgba(0,0,0,.04); color: var(--ct-header-text); }
.ct-close-abs {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
}

/* =========================================
 *  连接中占位
 * ========================================= */
.ct-connecting {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 32px;
  min-height: 320px;
  background: var(--ct-panel-bg);
}
.ct-connecting-text {
  font-size: 14px;
  color: var(--ct-muted);
  letter-spacing: 0.5px;
}
.ct-connecting-err {
  font-size: 15px;
  color: var(--ct-header-text);
  font-weight: 500;
}
.ct-connecting-sub {
  font-size: 13px;
  color: var(--ct-muted);
}

.ct-dots {
  display: flex;
  gap: 8px;
}
.ct-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--ct-brand);
  animation: ct-dot-jump 1.2s ease-in-out infinite;
}
.ct-dots span:nth-child(2) { animation-delay: 0.15s; }
.ct-dots span:nth-child(3) { animation-delay: 0.3s; }
@keyframes ct-dot-jump {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.45;
  }
  30% {
    transform: translateY(-9px);
    opacity: 1;
  }
}

.ct-panel-ready {
  animation: ct-fade-in 260ms ease-out;
}
@keyframes ct-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* =========================================
 *  消息区
 * ========================================= */
.ct-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 4px;
  background: var(--ct-body-bg);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ct-body::-webkit-scrollbar { width: 6px; }
.ct-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,.12); border-radius: 3px; }

.ct-welcome {
  align-self: center;
  max-width: 90%;
  padding: 8px 12px;
  color: var(--ct-muted);
  font-size: 12px;
  text-align: center;
  margin: 8px 0;
}

.ct-msg { display: flex; gap: 8px; margin-top: 4px; align-items: flex-start; }
.ct-msg.same { margin-top: 2px; }
.ct-msg-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, var(--ct-brand), #0AC29D);
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 600; flex-shrink: 0;
  overflow: hidden;
  /* 嵌入式 widget：气泡首行居中 */
  margin-top: 2px;
}
/* 独立对话页：字体和布局略大，头像下移一点视觉更居中 */
.ct-panel-page .ct-msg-avatar { margin-top: 7px; }
/* 访客头像用 SVG，无需底色，避免圆形背景显得多余 */
.ct-msg-avatar.self {
  background: transparent;
}
.ct-msg-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ct-msg-avatar.hidden { visibility: hidden; }
.ct-msg-bubble {
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--ct-agent-bubble);
  border: 1px solid var(--ct-agent-bubble-border);
  color: var(--ct-agent-bubble-text);
  word-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.55;
  min-height: 32px;
  display: flex;
  align-items: center;
}
.ct-msg.self { flex-direction: row-reverse; }
.ct-msg.self .ct-msg-bubble {
  background: var(--ct-self-bubble);
  color: var(--ct-self-bubble-text);
  border-color: transparent;
}
.ct-msg-time {
  align-self: center;
  color: var(--ct-muted);
  font-size: 11px;
  padding: 8px 0 4px;
}

/* 系统消息（警告 / 封禁提示）：居中警示样式 */
.ct-msg-system {
  align-self: center;
  max-width: 88%;
  margin: 8px 0;
  padding: 10px 14px;
  background: rgba(250, 173, 20, 0.12);
  border: 1px solid rgba(250, 173, 20, 0.35);
  color: #a55a00;
  font-size: 12px;
  border-radius: 8px;
  text-align: center;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

/* =========================================
 *  FAQ 胶囊
 * ========================================= */
.ct-faq {
  border-top: 1px solid var(--ct-input-border-top);
  padding: 8px 12px;
  background: var(--ct-input-bg);
  flex-shrink: 0;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
}
.ct-faq::-webkit-scrollbar { display: none; }
.ct-faq-label {
  display: inline-block;
  color: var(--ct-muted);
  font-size: 11px;
  margin-right: 6px;
  vertical-align: middle;
}
.ct-faq-pill {
  display: inline-block;
  padding: 6px 12px;
  margin: 2px 6px 2px 0;
  border: 1px solid var(--ct-faq-border);
  border-radius: 16px;
  background: var(--ct-faq-bg);
  color: var(--ct-faq-text);
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.1s, background 0.15s;
}
.ct-faq-pill:hover { background: var(--ct-faq-hover-bg); }
.ct-faq-pill:active { transform: scale(0.96); }

/* =========================================
 *  输入区
 * ========================================= */
.ct-input {
  border-top: 1px solid var(--ct-input-border-top);
  padding: 10px 12px 12px;
  background: var(--ct-input-bg);
  display: flex;
  gap: 6px;
  align-items: flex-end;
  flex-shrink: 0;
}

.ct-attach {
  height: 34px;
  width: 34px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: var(--ct-attach-color);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .15s;
  flex-shrink: 0;
}
.ct-attach:hover:not(:disabled) {
  background: var(--ct-attach-hover-bg);
  color: var(--ct-brand);
}
.ct-attach:disabled { cursor: not-allowed; opacity: 0.5; }
.ct-attach svg {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  stroke-width: 1.6;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.ct-upload-error {
  padding: 6px 12px;
  background: rgba(250, 81, 81, 0.08);
  color: #FA5151;
  font-size: 12px;
  text-align: center;
  border-top: 1px solid var(--ct-input-border-top);
}

.ct-pending {
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid var(--ct-input-border-top);
  background: var(--ct-textarea-bg);
  overflow-x: auto;
  scrollbar-width: thin;
}
.ct-pending::-webkit-scrollbar { height: 4px; }
.ct-pending::-webkit-scrollbar-thumb { background: rgba(0,0,0,.15); border-radius: 2px; }

.ct-pending-item {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: var(--ct-panel-bg);
}
.ct-pending-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ct-pending-item.status-uploading img { opacity: .55; }
.ct-pending-item.status-failed img { opacity: .4; }

.ct-pending-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  text-align: center;
  background: rgba(0, 0, 0, 0.35);
  padding: 4px;
  line-height: 1.2;
  pointer-events: none;
}
.ct-pending-overlay.failed {
  background: rgba(250, 81, 81, 0.7);
}

.ct-pending-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.ct-pending-remove:hover { background: rgba(0, 0, 0, 0.75); }

.ct-msg-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  min-width: 0;
  max-width: 74%;
}
.ct-msg.self .ct-msg-content { align-items: flex-end; }

.ct-msg-image-link {
  display: block;
  border-radius: 8px;
  overflow: hidden;
  cursor: zoom-in;
  max-width: 220px;
}
.ct-msg-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: 240px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  background: rgba(0,0,0,.04);
}
.ct-textarea {
  flex: 1;
  min-height: 34px;
  max-height: 96px;
  padding: 7px 12px;
  border: 1px solid var(--ct-textarea-border);
  border-radius: 6px;
  background: var(--ct-textarea-bg);
  color: var(--ct-textarea-text);
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  resize: none;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}
.ct-textarea::placeholder { color: var(--ct-muted); }
.ct-textarea:focus {
  border-color: var(--ct-textarea-focus);
  background: var(--ct-textarea-focus-bg);
}
.ct-send {
  height: 34px;
  width: 34px;
  border: none;
  border-radius: 6px;
  background: var(--ct-send-disabled-bg);
  color: var(--ct-send-disabled-color);
  cursor: not-allowed;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ct-send svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
  stroke-width: 1.6;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ct-send.active {
  background: var(--ct-send-bg);
  color: var(--ct-send-color);
  cursor: pointer;
}
.ct-send.active:hover { background: var(--ct-send-hover-bg); }
.ct-send.active:active { transform: scale(0.95); }

.ct-error {
  padding: 24px 20px;
  color: var(--ct-muted);
  text-align: center;
  font-size: 13px;
}
`;
